import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Users, CreditCard, TrendingUp, Clock, XCircle, Mail, AlertTriangle } from 'lucide-react';

function StatCard({ icon: Icon, label, value, color, sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">{label}</p>
      </div>
      <p className="text-3xl font-extrabold text-slate-900 font-heading">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}

function statusBadge(status) {
  const map = {
    active: 'bg-emerald-100 text-emerald-700',
    trial: 'bg-blue-100 text-blue-700',
    trial_expired: 'bg-amber-100 text-amber-700',
    cancelled: 'bg-red-100 text-red-700',
  };
  return <Badge className={`text-[10px] ${map[status] || 'bg-slate-100 text-slate-600'}`}>{status?.replace('_', ' ')}</Badge>;
}

function planBadge(planId) {
  if (planId === 'pro') return <Badge className="bg-slate-900 text-white text-[10px]">Pro</Badge>;
  if (planId === 'standard') return <Badge className="bg-slate-200 text-slate-800 text-[10px]">Standard</Badge>;
  return <Badge variant="outline" className="text-[10px]">Free Trial</Badge>;
}

export default function SubscriptionsDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/subscription/admin/stats')
      .then(r => setStats(r.data))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" /></div>;
  if (!stats) return <div className="text-center py-16 text-slate-400">Failed to load subscription data</div>;

  return (
    <div data-testid="subscriptions-dashboard">
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold font-heading text-slate-900 tracking-tight">Subscriptions</h1>
        <p className="text-sm text-slate-500 mt-1">Track subscriber activity, plans, and revenue</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Users} label="Total Users" value={stats.total_users} color="bg-slate-700" />
        <StatCard icon={CreditCard} label="Standard" value={stats.active_standard} color="bg-slate-500" sub="R250/mo" />
        <StatCard icon={CreditCard} label="Pro" value={stats.active_pro} color="bg-slate-900" sub="R500/mo" />
        <StatCard icon={TrendingUp} label="MRR" value={`R${stats.mrr.toLocaleString()}`} color="bg-emerald-600" sub="Monthly recurring" />
        <StatCard icon={Clock} label="Free Trial" value={stats.free_trial} color="bg-blue-500" sub="Active trials" />
        <StatCard icon={AlertTriangle} label="Trial Expired" value={stats.trial_expired} color="bg-amber-500" sub="Awaiting payment" />
        <StatCard icon={XCircle} label="Cancelled" value={stats.cancelled} color="bg-red-500" />
        <StatCard icon={Mail} label="Emails Sent" value={stats.emails_sent} color="bg-indigo-500" sub={stats.emails_pending > 0 ? `${stats.emails_pending} pending` : ''} />
      </div>

      {/* Subscribers Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">All Subscribers</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="subscribers-table">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="px-5 py-3 text-xs font-bold text-slate-500 uppercase">User</th>
                <th className="px-5 py-3 text-xs font-bold text-slate-500 uppercase">Role</th>
                <th className="px-5 py-3 text-xs font-bold text-slate-500 uppercase">Plan</th>
                <th className="px-5 py-3 text-xs font-bold text-slate-500 uppercase">Status</th>
                <th className="px-5 py-3 text-xs font-bold text-slate-500 uppercase">Trial / Paid Until</th>
                <th className="px-5 py-3 text-xs font-bold text-slate-500 uppercase">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(stats.subscribers || []).map((s, i) => (
                <tr key={i} className="hover:bg-slate-50/50" data-testid={`subscriber-row-${i}`}>
                  <td className="px-5 py-3">
                    <p className="font-medium text-slate-900">{s.user_name || 'Unknown'}</p>
                    <p className="text-xs text-slate-400">{s.user_email}</p>
                  </td>
                  <td className="px-5 py-3">
                    <Badge variant="outline" className="text-[10px]">{s.user_role}</Badge>
                  </td>
                  <td className="px-5 py-3">{planBadge(s.plan_id)}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      {statusBadge(s.status)}
                      {s.days_remaining !== undefined && s.status === 'trial' && (
                        <span className="text-[10px] text-blue-600 font-medium">{s.days_remaining}d left</span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-500">
                    {s.paid_until ? new Date(s.paid_until).toLocaleDateString() :
                     s.trial_end ? new Date(s.trial_end).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-5 py-3 text-xs font-medium text-slate-700">
                    {s.amount ? `R${s.amount}` : 'Free'}
                  </td>
                </tr>
              ))}
              {(!stats.subscribers || stats.subscribers.length === 0) && (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">No subscribers yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
